import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

export default function TermsOfService() {
  useSEO({
    title: 'Terms of Service | GreenTick AI - User Agreement',
    description: 'Review the terms and conditions for using GreenTick AI services. Learn about your rights and responsibilities on our platform.',
    canonicalPath: '/terms-of-service',
  });
  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <main className="pt-32 pb-20 relative overflow-hidden home-section-bg">
        {/* Global Grid Overlay */}
        <div className="absolute inset-0 bg-grid-pattern opacity-60 z-0 pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8">
              <span>← Back to Home</span>
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center">
                <FileText className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white">
                Terms of Service
              </h1>
            </div>

            <p className="text-slate-400 mb-12">Last Updated: January 2026</p>

            <div className="space-y-8">
              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Acceptance of Terms</h2>
                <p className="text-slate-300 leading-relaxed">
                  By accessing or using GreenTick's services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this platform.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Description of Service</h2>
                <p className="text-slate-300 leading-relaxed">
                  GreenTick provides an AI-powered recruitment platform that offers resume screening, interactive interviews, candidate assessment, and evaluation reporting services. Our platform helps organizations streamline their hiring process and helps candidates prepare for interviews.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">User Accounts</h2>
                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p>When creating an account, you agree to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide accurate, current, and complete information</li>
                    <li>Maintain and update your information to keep it accurate</li>
                    <li>Maintain the security of your password and account</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized use</li>
                  </ul>
                </div>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Acceptable Use</h2>
                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p>You agree not to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Use the service for any illegal or unauthorized purpose</li>
                    <li>Attempt to gain unauthorized access to any part of the service</li>
                    <li>Interfere with or disrupt the service or servers</li>
                    <li>Upload malicious code or attempt to breach security</li>
                    <li>Impersonate another person or entity</li>
                    <li>Collect or harvest information about other users</li>
                  </ul>
                </div>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Intellectual Property</h2>
                <p className="text-slate-300 leading-relaxed">
                  The service and its original content, features, and functionality are owned by GreenTick and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Data Usage and Privacy</h2>
                <p className="text-slate-300 leading-relaxed">
                  Your use of the service is also governed by our <Link to="/privacy_policy" className="text-teal-400 hover:text-teal-300">Privacy Policy</Link>. By using GreenTick, you consent to the collection and use of information as described in our Privacy Policy.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Subscription and Payment</h2>
                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p>For paid services:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Fees are charged in advance on a subscription basis</li>
                    <li>Subscriptions automatically renew unless cancelled</li>
                    <li>Refunds are subject to our refund policy</li>
                    <li>We reserve the right to change pricing with notice</li>
                  </ul>
                </div>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Termination</h2>
                <p className="text-slate-300 leading-relaxed">
                  We may terminate or suspend your account and access to the service immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the service will immediately cease.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
                <p className="text-slate-300 leading-relaxed">
                  GreenTick shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the service. Our total liability shall not exceed the amount paid by you for the service in the past 12 months.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Changes to Terms</h2>
                <p className="text-slate-300 leading-relaxed">
                  We reserve the right to modify these terms at any time. We will notify users of any material changes. Your continued use of the service after such modifications constitutes acceptance of the updated terms.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Contact Information</h2>
                <p className="text-slate-300 leading-relaxed">
                  For questions about these Terms of Service, please contact us at{' '}
                  <a href="mailto:legal@greentick.org.in" className="text-teal-400 hover:text-teal-300">
                    legal@greentick.org.in
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
